import moment from 'moment';
import React, { useState } from 'react';
import PropTypes, { any } from 'prop-types';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import SportLabel from '../../../../../cards/CardComponents/SportLabel';

import config from '../../../../../../../config';
import styles from './leagueSessionCard.scss';
import { sessionTypes, tennisScoringSetTypes, pickleballScoringGameTypes, ageDisplay, ratingTypes, SanctionTypes } from '../../../../../../utils/constants';
import { sportTypeIds } from '../../../../../../utils/eventConstants';

const LeagueSessionCard = ({ league, onContactClick }) => {
    const history = useHistory();
    const user = useSelector(({ auth }) => auth.user);
    const { USAP } = SanctionTypes;
    const { conferenceMinAge, conferenceMaxAge, sessionTypeId, singlesMatchCount, doublesMatchCount, singlesScoringFormatTypeId, doublesScoringFormatTypeId, conferenceOrganizer, teams, sessionId, sessionLocation, sanctionTypeId = null } = league;
    const { email: organizerEmail, firstName: organizerFirstName, lastName: organizerLastName, phone: organizerPhone, memberId: organizerMemberId } = conferenceOrganizer;
    const { formattedAddress, placeName } = sessionLocation || {};
    const [expandTeams, setExpandTeams] = useState();
    const [expandContact, setExpandContact] = useState();
    const isTennisMatch = league?.sportTypeId === sportTypeIds.TENNIS;
    const isUSAPSanctionedSession = sanctionTypeId === USAP;

    const toggleTeams = () => {
        setExpandTeams(!expandTeams);
    };
    const toggleContact = () => {
        if (!user) {
            onContactClick();
        } else {
            setExpandContact(!expandContact);
        }
    };

    const handleOnContactClick = () => {
        if (!user) {
            onContactClick();
        } else {
            setExpandContact(true);
        }
    };

    const sendMessage = () => {
        history.push(`/webview/messaging/${user.id}?rid=${organizerMemberId}&rname=${organizerFirstName}`);
    };

    const scoringTypes = isTennisMatch ? tennisScoringSetTypes : pickleballScoringGameTypes;
    const singlesScoringText = scoringTypes.find(item => item.id === singlesScoringFormatTypeId)?.description;
    const doublesScoringText = scoringTypes.find(item => item.id === doublesScoringFormatTypeId)?.description;
    const sessionText = sessionTypes.find(item => item.id === sessionTypeId)?.description;
    const conferenceRatingMin = isTennisMatch ? `${ratingTypes.tennis}: ${league?.conferenceRatings?.ratingMin}` : `${ratingTypes.pickleball}: ${league?.conferenceRatings?.ratingMin}`;
    const conferenceRatingMax = `${league?.conferenceRatings?.ratingMax}`;
    const ageRange = ageDisplay([conferenceMinAge, conferenceMaxAge]);

    let styledGender;
    switch (league?.genderType?.name) {
        case 'M':
            styledGender = 'Male';
            break;
        case 'F':
            styledGender = 'Female';
            break;
        default:
            styledGender = league?.genderType?.name;
            break;
    }

    return (
        <div className={`utr-card ${styles.leagueSessionCard}`}>
            <SportLabel sportTypeId={league?.sportTypeId} />
            <div className="container-flex">
                <div className="row">
                    <div className={`col-12 col-md-9 flex-col aifs ${styles.leftSide}`}>
                        <div className={styles.title}>
                            {league.sessionName}
                        </div>
                        <div className={styles.tagsContainer}>
                            <div className={styles.tag}>{conferenceRatingMin} - {conferenceRatingMax}</div>
                            <div className={styles.tag}>{`Ages: ${ageRange}`}</div>
                            <div className={styles.tag}>{styledGender}</div>
                            <div className={styles.tag}>{sessionText}</div>
                        </div>
                        <div className="row mt16 w-100">
                            <div className="col-12 col-md-4 flex-col aifs">
                                <div className={styles.sectionTitle}>
                                    Match Format
                                </div>
                                <div className={styles.sectionContent}>
                                    <div>{singlesMatchCount !== 0 && `${singlesMatchCount} Singles - ${singlesScoringText}`}</div>
                                    <div>{doublesMatchCount !== 0 && `${doublesMatchCount} Doubles - ${doublesScoringText}`}</div>
                                </div>
                            </div>
                            <div className="col-12 col-md-3 flex-col aifs">
                                <div className={styles.sectionTitle}>
                                    Dates
                                </div>
                                <div className={styles.sectionContent}>
                                    <div>{moment.utc(league.sessionStartDateUtc).format('MM/DD/YY')} - {moment.utc(league.sessionEndDateUtc).format('MM/DD/YY')}</div>
                                </div>
                            </div>
                            {formattedAddress && placeName && (
                                <div className="col-12 col-md-4 flex-col aifs">
                                    <div className={styles.sectionTitle}>
                                        Location
                                    </div>
                                    <div className={styles.sectionContent}>
                                        <div>{placeName}</div>
                                        <div className="link clickable">
                                            <a href={`https://www.google.com/maps/search/?api=1&query=${formattedAddress}`}>
                                                {formattedAddress}
                                            </a>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                        <div className="row mt16 d-none d-md-block">
                            <div className="col-12">
                                <div className="d-flex aic">
                                    <a href={`${config.leaguesAppHost}/leagues/session/${sessionId}`}><div className="btn btn-xl btn-primary text-uppercase">View all details</div></a>
                                    {isUSAPSanctionedSession && (<img className="ml8" width={76} height={50} src="/img/icons/USAPickleball_SanctionedEventLogo.png" />)}
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className={`col-12 col-md-3 flex-col aifs ${styles.rightSide}`}>
                        {teams.length > 0 && (
                            <>
                                <button type="button" className={`${styles.joinButton}`} onClick={toggleTeams}>
                                    <div className={`${styles.sectionTitle} ${styles.btMobile}`}>
                                        Join a team ({teams.length}) <i className={`fal fa-chevron-${expandTeams ? 'up' : 'down'} ml8 d-block d-md-none`} />
                                    </div>
                                </button>
                                <div className={styles.sectionContent}>
                                    <div className="d-block d-md-none">
                                        {expandTeams && (
                                            <div className="mt8">
                                                {teams.map(team => {
                                                    return (
                                                        <div key={team.id} className="link clickable"><a href={`${config.leaguesAppHost}/teams/${team.id}`}>{team.name}</a></div>
                                                    );
                                                })}

                                            </div>
                                        )}
                                    </div>
                                    <div className="d-none d-md-block mt8">
                                        {teams[0] && <div className="link clickable"><a href={`${config.leaguesAppHost}/teams/${teams[0].id}`}>{teams[0].name}</a></div>}
                                        {teams[1] && <div className="link clickable"><a href={`${config.leaguesAppHost}/teams/${teams[1].id}`}>{teams[1].name}</a></div>}
                                        {teams[2] && <div className="link clickable"><a href={`${config.leaguesAppHost}/teams/${teams[2].id}`}>{teams[2].name}</a></div>}

                                        {teams.length > 3 && !expandTeams && (
                                            <div className="link clickable" onClick={toggleTeams}>+ {teams.length - 3} more</div>
                                        )}
                                        {expandTeams && (
                                        <>
                                            {teams.map((team, i) => {
                                                if (i === 0 || i === 1 || i === 2) return;
                                                return (
                                                    <div key={team.id} className="link clickable"><a href={`${config.leaguesAppHost}/teams/${team.id}`}>{team.name}</a></div>
                                                );
                                            })}
                                            <div className="link clickable mt8" onClick={() => setExpandTeams(false)}>Show less</div>
                                        </>
                                        )}
                                    </div>
                                </div>
                                <hr className="div bt0 d-none d-md-block" />
                            </>
                        )}
                        <div className={`${styles.sectionTitle} ${styles.btMobile}`} onClick={toggleContact}>
                            Want to start a team?
                        </div>
                        <div className={styles.sectionContent}>
                            {!expandContact && (
                                <div className="d-none d-md-block mt8"><span className="link clickable" onClick={handleOnContactClick}>Contact {organizerFirstName} {organizerLastName}</span> to captain your own team. </div>
                            )}

                            {expandContact && (
                                <div className="mt8">
                                    <div>Contact {organizerFirstName} {organizerLastName} to captain your own team.</div>
                                    <div className="link clickable"><a href={`tel:${organizerPhone}`}>{organizerPhone}</a></div>
                                    <div className="link clickable"><a href={`mailto:${organizerEmail}`}>{organizerEmail}</a></div>
                                    <div onClick={sendMessage} className="link clickable">Message {organizerFirstName}</div>
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="col-12 mt16 d-block d-md-none">
                        <div className="bt pt16">
                            <div className="d-flex aic">
                                <a href={`${config.leaguesAppHost}/leagues/session/${sessionId}`}><div className="btn btn-xl btn-primary text-uppercase">View all details</div></a>
                                {isUSAPSanctionedSession && (<img className="ml8" width={76} height={50} src="/img/icons/USAPickleball_SanctionedEventLogo.png" />)}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

LeagueSessionCard.propTypes = {
  league: PropTypes.shape({
    conferenceMinAge: PropTypes.number.isRequired,
    conferenceMaxAge: PropTypes.number.isRequired,
    conferenceRatings: PropTypes.shape({
      ratingMax: PropTypes.number.isRequired,
      ratingMin: PropTypes.number.isRequired,
    }).isRequired,
    sessionTypeId: PropTypes.number.isRequired,
    singlesMatchCount: PropTypes.number.isRequired,
    doublesMatchCount: PropTypes.number.isRequired,
    singlesScoringFormatTypeId: PropTypes.number.isRequired,
    doublesScoringFormatTypeId: PropTypes.number.isRequired,
    conferenceOrganizer: PropTypes.shape({
        email: PropTypes.string.isRequired,
        firstName: PropTypes.string.isRequired,
        lastName: PropTypes.string.isRequired,
        phone: PropTypes.string.isRequired,
        memberId: PropTypes.number.isRequired,
    }).isRequired,
    teams: PropTypes.arrayOf({
        id: PropTypes.number.isRequired,
        name: PropTypes.string.isRequired,
    }).isRequired,
    sessionId: PropTypes.number.isRequired,
    sessionLocation: PropTypes.objectOf(any),
    sessionName: PropTypes.string.isRequired,
    sanctionTypeId: PropTypes.number.isRequired,
  }).isRequired,
  onContactClick: PropTypes.func.isRequired,
};

export default LeagueSessionCard;
